import React, { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import AlertCircleOutline from "mdi-material-ui/AlertCircleOutline";
import { isNil } from "@salespark/toolkit";
import { course_lesson_questionnaire_get, course_lesson_questionnaire_post } from "src/api/courses";
import { useAuth } from "src/hooks/useAuth";
import QuestionnaireSuccessCard from "./QuestionnaireSuccessCard";

const QUESTION_TYPE_LABELS = {
  text: "Resposta curta",
  paragraph: "Parágrafo",
  dropdown: "Lista pendente",
  multiselect: "Multisseleção",
  switch: "Interruptor",
  multiple: "Escolha múltipla",
  multiple_any: "Escolha única (várias corretas)",
  checkbox: "Caixa de verificação",
  checkboxes: "Caixas de verificação",
};
const SHOW_QUESTION_TYPE_LABELS = false;

/** Card shadow for each question (colour + 0/4px offset; blur avoids a hard “second block” under the card). */
const QUESTIONNAIRE_QUESTION_CARD_BOX_SHADOW = "0px 4px 12px rgba(38, 43, 67, 0.16)";
const QUESTIONNAIRE_CARDS_MARGIN_BOTTOM = { xs: 3, sm: 5 };

const QUESTIONNAIRE_HEADER_CARD_SX = {
  p: 6,
  mb: QUESTIONNAIRE_CARDS_MARGIN_BOTTOM,
  borderRadius: "8px",
  bgcolor: "background.paper",
  boxShadow: QUESTIONNAIRE_QUESTION_CARD_BOX_SHADOW,
};

/** Map API questionnaire.type to learner-facing copy; use "questionário" only when type is general. */
const resolveQuestionnaireKind = (type) => {
  if (type === "evaluation" || type === "feedback" || type === "diagnostic") {
    return type;
  }
  return "general";
};

const QUESTIONNAIRE_LEARNER_LABELS = {
  general: {
    loadError: "Erro ao carregar o questionário.",
    emptyLesson: "Sem conteúdo para esta aula.",
    noQuestions: "Este questionário ainda não tem perguntas.",
    filledHeadline: "Este questionário já foi preenchido.",
    successHeadline: "Respostas submetidas com sucesso.",
    submitButton: "Submeter respostas",
    dialogTitle: "Submeter respostas",
    dialogBody: "Tens a certeza que queres submeter as tuas respostas? Depois de confirmar, a submissão fica registada.",
    submitFailFallback: "Não foi possível submeter as respostas.",
  },
  evaluation: {
    loadError: "Erro ao carregar a avaliação.",
    emptyLesson: "Sem conteúdo para esta aula.",
    noQuestions: "Esta avaliação ainda não tem perguntas.",
    filledHeadline: "Esta avaliação já foi preenchida.",
    successHeadline: "A avaliação foi submetida com sucesso.",
    submitButton: "Submeter avaliação",
    dialogTitle: "Submeter avaliação",
    dialogBody: "Tens a certeza que queres submeter a avaliação? Depois de confirmar, a submissão fica registada.",
    submitFailFallback: "Não foi possível submeter a avaliação.",
  },
  feedback: {
    loadError: "Erro ao carregar o feedback.",
    emptyLesson: "Sem conteúdo para esta aula.",
    noQuestions: "Este feedback ainda não tem perguntas.",
    filledHeadline: "Este feedback já foi submetido.",
    successHeadline: "O feedback foi submetido com sucesso.",
    submitButton: "Submeter feedback",
    dialogTitle: "Submeter feedback",
    dialogBody: "Tens a certeza que queres submeter o feedback? Depois de confirmar, a submissão fica registada.",
    submitFailFallback: "Não foi possível submeter o feedback.",
  },
  diagnostic: {
    loadError: "Erro ao carregar o diagnóstico.",
    emptyLesson: "Sem conteúdo para esta aula.",
    noQuestions: "Este diagnóstico ainda não tem perguntas.",
    filledHeadline: "Este diagnóstico já foi preenchido.",
    successHeadline: "O diagnóstico foi submetido com sucesso.",
    submitButton: "Submeter diagnóstico",
    dialogTitle: "Submeter diagnóstico",
    dialogBody: "Tens a certeza que queres submeter o diagnóstico? Depois de confirmar, a submissão fica registada.",
    submitFailFallback: "Não foi possível submeter o diagnóstico.",
  },
};

const getQuestionnaireLearnerLabels = (type) => QUESTIONNAIRE_LEARNER_LABELS[resolveQuestionnaireKind(type)];

const buildQuestionnaireScoreDisplayPT = (questionnaire, result) => {
  if (questionnaire?.show_results !== true || questionnaire?.is_scored !== true || isNil(result)) {
    return null;
  }
  const totalPoints = typeof result.total_points === "number" ? result.total_points : 0;
  if (totalPoints <= 0) {
    return null;
  }
  const score = typeof result.score === "number" ? result.score : 0;
  const percentRaw = (score / totalPoints) * 100;
  const percentStr = String(parseFloat(percentRaw.toFixed(2)));
  return {
    pointsLine: `${score} / ${totalPoints}`,
    percentLine: `${percentStr}% de sucesso!`,
  };
};
const getReviewSectionTitle = (reviewFilter) => {
  if (reviewFilter === "correct") {
    return "Resultado do questionário: respostas certas";
  }

  if (reviewFilter === "incorrect") {
    return "Resultado do questionário: respostas erradas";
  }

  return "Resultado do questionário";
};

const normalizeOptions = (options) => (Array.isArray(options) ? options.map((opt) => ({ id: String(opt?.id ?? ""), label: String(opt?.label ?? "") })) : []);

/******************************************************************
 * Client-side option resolution (aligned with server grading).
 ******************************************************************/
const resolveToOptionIdClient = (token, options, validation) => {
  const trimEnabled = validation?.trim !== false;
  const caseSensitive = validation?.case_sensitive === true;
  let value = String(token ?? "");
  if (trimEnabled) {
    value = value.trim();
  }
  const normalize = (a, b) => {
    if (caseSensitive) {
      return a === b;
    }
    return String(a).toLowerCase() === String(b).toLowerCase();
  };
  for (const opt of options) {
    if (normalize(value, opt.id) || normalize(value, opt.label)) {
      return String(opt.id);
    }
  }
  return null;
};

const isRequiredAnswerMissingClient = (question, userAnswer) => {
  const required = question?.required !== false;
  if (required === false) {
    return false;
  }

  const type = question?.type;
  const options = normalizeOptions(question?.options);
  const asArray = Array.isArray(userAnswer) ? userAnswer : userAnswer == null ? [] : [userAnswer];

  if (type === "text" || type === "paragraph") {
    const text = String(asArray[0] ?? "").trim();
    return text.length === 0;
  }

  if (type === "dropdown" || type === "multiple" || type === "multiple_any") {
    if (asArray.length === 0 || asArray[0] === "" || asArray[0] == null) {
      return true;
    }
    const resolved = resolveToOptionIdClient(asArray[0], options, question?.validation || {});
    return resolved == null;
  }

  if (type === "multiselect" || type === "checkboxes") {
    if (asArray.length === 0) {
      return true;
    }
    const resolved = asArray.map((item) => resolveToOptionIdClient(item, options, question?.validation || {})).filter((id) => id != null);
    return resolved.length === 0;
  }

  if (type === "switch" || type === "checkbox") {
    const state = asArray[0];
    return state !== "checked" && state !== "unchecked";
  }

  if (Array.isArray(userAnswer)) {
    return userAnswer.length === 0;
  }
  return userAnswer == null || userAnswer === "";
};

const getQuestionKey = (question, questionIndex) => {
  if (question?.id != null && String(question.id) !== "") {
    return String(question.id);
  }
  if (question?._id != null && String(question._id) !== "") {
    return String(question._id);
  }
  return `__idx_${questionIndex}`;
};

const buildAnswersMapFromReview = (review) => {
  const reviewQuestions = Array.isArray(review?.questions) ? review.questions : [];
  return reviewQuestions.reduce((acc, item) => {
    if (item?.question_id == null) {
      return acc;
    }
    const answer = item.answer;
    acc[String(item.question_id)] = Array.isArray(answer) ? answer : answer == null ? [] : [answer];
    return acc;
  }, {});
};

const getReviewQuestionMeta = (reviewQuestion) => {
  if (reviewQuestion == null) {
    return null;
  }

  if (reviewQuestion.has_validation !== true) {
    return {
      label: "Sem validação",
      color: "default",
    };
  }

  return {
    label: reviewQuestion.is_correct === true ? "Correta" : "Incorreta",
    color: reviewQuestion.is_correct === true ? "success" : "error",
  };
};

const formatCorrectAnswersText = (question) => {
  const correctAnswers = Array.isArray(question?.correct_answers) ? question.correct_answers : [];
  if (correctAnswers.length === 0) {
    return "";
  }

  const type = question?.type;
  const options = normalizeOptions(question?.options);
  const optionLabelById = options.reduce((acc, option) => {
    acc[String(option.id)] = option.label;
    return acc;
  }, {});

  if (type === "switch" || type === "checkbox") {
    return correctAnswers.map((answer) => (String(answer) === "checked" ? "Sim" : "Nao")).join(", ");
  }

  if (type === "dropdown" || type === "multiselect" || type === "multiple" || type === "multiple_any" || type === "checkboxes") {
    return correctAnswers.map((answer) => optionLabelById[String(answer)] || String(answer)).join(", ");
  }

  return correctAnswers.join(", ");
};

function QuestionnaireFilledNotice({ headline, questionnaireTitle, compact = false, scoreDisplay = null }) {
  return (
    <Box
      className="questionnaire-filled-notice-wrap"
      sx={{
        width: 1,
        boxSizing: "border-box",
        pt: compact ? 0 : undefined,
        pb: compact ? 4 : undefined,
        mb: compact ? 3 : undefined,
      }}
    >
      <QuestionnaireSuccessCard questionnaireTitle={questionnaireTitle} headline={headline} />
      {scoreDisplay != null && typeof scoreDisplay.pointsLine === "string" && scoreDisplay.pointsLine !== "" ? (
        <Box
          sx={{
            width: 1,
            maxWidth: 720,
            mx: "auto",
            mt: { xs: 2.5, sm: 10 },
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <Typography component="h2" variant="h5" sx={{ m: 0 }}>
            PONTOS
          </Typography>
          <Typography component="h1" variant="h3" sx={{ fontWeight: 700, m: 0 }}>
            {scoreDisplay.pointsLine}
          </Typography>
          {typeof scoreDisplay.percentLine === "string" && scoreDisplay.percentLine !== "" ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0 }}>
              {scoreDisplay.percentLine}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function QuestionBlock({ question, value, onChange, fieldGroupId, errorHelperText, readOnly = false, reviewMeta = null }) {
  const type = question?.type;
  const options = normalizeOptions(question?.options);
  const title = question?.title ?? "";
  const description = question?.description ?? "";

  const handleTextChange = (event) => {
    onChange([event.target.value]);
  };

  const helper = errorHelperText ? <FormHelperText error>{errorHelperText}</FormHelperText> : null;
  const reviewChip = reviewMeta ? <Chip label={reviewMeta.label} size="small" color={reviewMeta.color} /> : null;
  const showCorrectAnswerCard = readOnly === true && reviewMeta?.color === "error";
  const correctAnswerText = showCorrectAnswerCard ? formatCorrectAnswersText(question) : "";
  const correctAnswerNotice =
    showCorrectAnswerCard && correctAnswerText ? (
      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 1.25,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "info.light",
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
        }}
      >
        <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5, color: "info.dark", textTransform: "uppercase" }}>
          Resposta correta
        </Typography>
        <Typography variant="body2" color="text.primary">
          {correctAnswerText}
        </Typography>
      </Box>
    ) : null;

  if (type === "text") {
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          size="small"
          placeholder="Resposta curta"
          value={value?.[0] ?? ""}
          onChange={handleTextChange}
          error={Boolean(errorHelperText)}
          disabled={readOnly}
        />
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "paragraph") {
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Parágrafo"
          value={value?.[0] ?? ""}
          onChange={handleTextChange}
          error={Boolean(errorHelperText)}
          disabled={readOnly}
        />
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "dropdown") {
    const selected = value?.[0] ?? "";
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <FormControl fullWidth size="small" error={Boolean(errorHelperText)}>
          <InputLabel id={`q-${question.id}-dropdown-label`}>Selecione uma opção</InputLabel>
          <Select
            labelId={`q-${question.id}-dropdown-label`}
            label="Selecione uma opção"
            value={selected}
            onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
            disabled={readOnly}
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {helper}
        </FormControl>
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <FormControl fullWidth size="small" error={Boolean(errorHelperText)}>
          <InputLabel id={`q-${question.id}-multi-label`}>Selecione as opções</InputLabel>
          <Select
            labelId={`q-${question.id}-multi-label`}
            label="Selecione as opções"
            multiple
            value={selected}
            onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            disabled={readOnly}
            renderValue={(selectedIds) =>
              options
                .filter((opt) => selectedIds.includes(opt.id))
                .map((opt) => opt.label)
                .join(", ")
            }
          >
            {options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {helper}
        </FormControl>
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "switch") {
    const checked = value?.[0] === "checked";
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Switch
            checked={checked}
            onChange={(e) => onChange([e.target.checked ? "checked" : "unchecked"])}
            color={errorHelperText ? "error" : "primary"}
            disabled={readOnly}
          />
          <span>{title}</span>
        </label>
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "multiple" || type === "multiple_any") {
    const selected = value?.[0] ?? "";
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <RadioGroup name={fieldGroupId} value={selected} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.id}
              value={opt.id}
              control={<Radio color={errorHelperText ? "error" : "primary"} disabled={readOnly} />}
              label={opt.label}
              disabled={readOnly}
            />
          ))}
        </RadioGroup>
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "checkbox") {
    const checked = value?.[0] === "checked";
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => onChange([e.target.checked ? "checked" : "unchecked"])}
              name={question.id}
              color={errorHelperText ? "error" : "primary"}
              disabled={readOnly}
            />
          }
          label={title}
          disabled={readOnly}
        />
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  if (type === "checkboxes") {
    const selectedSet = new Set(Array.isArray(value) ? value : []);
    const toggle = (optionId) => {
      const next = new Set(selectedSet);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        next.add(optionId);
      }
      onChange([...next]);
    };
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, m: 0, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {SHOW_QUESTION_TYPE_LABELS === true && <Chip label={QUESTION_TYPE_LABELS[type] || type} size="small" color="secondary" variant="outlined" />}
            {reviewChip}
          </Box>
        </Box>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.id}
              control={
                <Checkbox checked={selectedSet.has(opt.id)} onChange={() => toggle(opt.id)} color={errorHelperText ? "error" : "primary"} disabled={readOnly} />
              }
              label={opt.label}
              disabled={readOnly}
            />
          ))}
        </Box>
        {helper}
        {correctAnswerNotice}
      </Box>
    );
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Tipo de pergunta não suportado: {type}
    </Alert>
  );
}

function QuestionnaireLesson({ courseId, lessonId, lessonCompleted, onQuestionnaireSubmitSuccess }) {
  const auth = useAuth();
  const authUser = auth?.user;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [answersByQuestionId, setAnswersByQuestionId] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [reviewResultsVisible, setReviewResultsVisible] = useState(false);

  const resetState = useCallback(() => {
    setError(null);
    setQuestionnaire(null);
    setReviewData(null);
    setEnrollmentId(null);
    setAnswersByQuestionId({});
    setFieldErrors({});
    setConfirmOpen(false);
    setSubmitError(null);
    setSubmitSuccess(false);
    setErrorDialogOpen(false);
    setErrorDialogMessage("");
    setReviewFilter("all");
    setReviewResultsVisible(false);
  }, []);

  const loadQuestionnaireData = useCallback(
    async ({ preserveSubmitSuccess = false } = {}) => {
      if (isNil(courseId) || isNil(lessonId) || isNil(authUser)) {
        resetState();
        return;
      }

      setLoading(true);
      setError(null);
      setQuestionnaire(null);
      setReviewData(null);
      setEnrollmentId(null);
      setAnswersByQuestionId({});
      setFieldErrors({});
      setSubmitError(null);
      if (preserveSubmitSuccess !== true) {
        setSubmitSuccess(false);
      }
      setErrorDialogOpen(false);
      setErrorDialogMessage("");

      try {
        const response = await course_lesson_questionnaire_get(authUser, courseId, lessonId);
        const payload = response?.data;
        if (payload?.questionnaire) {
          setQuestionnaire(payload.questionnaire);
          setReviewData(payload.review ?? null);
          setEnrollmentId(payload.enrollment_id ?? null);
          setAnswersByQuestionId(buildAnswersMapFromReview(payload.review));
        } else {
          setError("Resposta inválida do servidor.");
        }
      } catch (err) {
        setError("Erro ao carregar o conteúdo.");
      } finally {
        setLoading(false);
      }
    },
    [authUser, courseId, lessonId, resetState],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (cancelled) {
        return;
      }
      await loadQuestionnaireData();
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [loadQuestionnaireData]);

  useEffect(() => {
    const reviewEligible =
      questionnaire?.show_correct_answers === true &&
      Array.isArray(reviewData?.questions) &&
      reviewData.questions.length > 0 &&
      (submitSuccess === true || lessonCompleted === true);
    if (reviewEligible !== true) {
      setReviewResultsVisible(false);
    }
  }, [questionnaire, reviewData, submitSuccess, lessonCompleted]);

  const handleAnswerChange = useCallback((questionId, nextValue) => {
    setAnswersByQuestionId((prev) => ({ ...prev, [questionId]: nextValue }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  const validateRequiredFields = useCallback(
    (questions) => {
      const nextErrors = {};
      let valid = true;
      questions.forEach((question, questionIndex) => {
        const key = getQuestionKey(question, questionIndex);
        const value = answersByQuestionId[key];
        if (isRequiredAnswerMissingClient(question, value)) {
          valid = false;
          nextErrors[key] = "Esta pergunta é obrigatória.";
        }
      });
      return { valid, nextErrors };
    },
    [answersByQuestionId],
  );

  const handleClickSubmit = useCallback(() => {
    setSubmitError(null);
    if (isNil(enrollmentId)) {
      setSubmitError("Inscrição (enrollment) em falta. Recarrega a página.");
      return;
    }
    const questions = Array.isArray(questionnaire?.questions) ? questionnaire.questions : [];
    const { valid, nextErrors } = validateRequiredFields(questions);
    if (valid === false) {
      setFieldErrors(nextErrors);
      let firstErrorKey = null;
      questions.forEach((question, questionIndex) => {
        const key = getQuestionKey(question, questionIndex);
        if (nextErrors[key] != null && firstErrorKey == null) {
          firstErrorKey = key;
        }
      });
      if (firstErrorKey != null) {
        const id = `questionnaire-field-${firstErrorKey}`;
        requestAnimationFrame(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
      return;
    }
    setFieldErrors({});
    setConfirmOpen(true);
  }, [enrollmentId, questionnaire, validateRequiredFields]);

  const handleConfirmClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setConfirmOpen(false);
  }, [isSubmitting]);

  const handleErrorDialogClose = useCallback(() => {
    setErrorDialogOpen(false);
    setErrorDialogMessage("");
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    if (isNil(authUser) || isNil(courseId) || isNil(lessonId) || isNil(enrollmentId) || isNil(questionnaire)) {
      return;
    }
    const questions = Array.isArray(questionnaire.questions) ? questionnaire.questions : [];
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const answers = questions.map((question, questionIndex) => {
        const key = getQuestionKey(question, questionIndex);
        return {
          question_id: question.id != null && String(question.id) !== "" ? String(question.id) : key,
          answer: answersByQuestionId[key],
        };
      });

      await course_lesson_questionnaire_post(authUser, courseId, lessonId, {
        enrollment_id: enrollmentId,
        answers,
      });

      setConfirmOpen(false);
      setSubmitSuccess(true);
      await loadQuestionnaireData({ preserveSubmitSuccess: true });
      if (typeof onQuestionnaireSubmitSuccess === "function" && lessonCompleted !== true) {
        onQuestionnaireSubmitSuccess();
      }
    } catch (err) {
      const submitFailFallback = getQuestionnaireLearnerLabels(questionnaire?.type).submitFailFallback;
      const raw = err?.response?.data?.message || err?.response?.data?.error || submitFailFallback;
      const text = typeof raw === "string" ? raw : submitFailFallback;
      setConfirmOpen(false);
      setErrorDialogMessage(text);
      setErrorDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [authUser, courseId, lessonId, enrollmentId, questionnaire, answersByQuestionId, onQuestionnaireSubmitSuccess, lessonCompleted, loadQuestionnaireData]);

  if (isNil(courseId) || isNil(lessonId)) {
    return (
      <Box sx={{ pt: 6, px: 2 }}>
        <Alert severity="info">Dados da aula em falta.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pt: 8, pb: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: 4, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (isNil(questionnaire)) {
    return (
      <Box sx={{ pt: 4, px: 2 }}>
        <Alert severity="info">{getQuestionnaireLearnerLabels("general").emptyLesson}</Alert>
      </Box>
    );
  }

  const learnerLabels = getQuestionnaireLearnerLabels(questionnaire.type);
  const questions = Array.isArray(questionnaire.questions) ? questionnaire.questions : [];
  const reviewQuestions = Array.isArray(reviewData?.questions) ? reviewData.questions : [];
  const reviewByQuestionId = reviewQuestions.reduce((acc, item) => {
    if (item?.question_id != null) {
      acc[String(item.question_id)] = item;
    }
    return acc;
  }, {});

  if (questions.length === 0) {
    return (
      <Box sx={{ pt: 4, px: 2 }}>
        <Typography variant="h6" gutterBottom>
          {questionnaire.title}
        </Typography>
        <Alert severity="info">{learnerLabels.noQuestions}</Alert>
      </Box>
    );
  }

  const reviewModeActive = questionnaire?.show_correct_answers === true && reviewQuestions.length > 0 && (submitSuccess === true || lessonCompleted === true);
  const filteredReviewQuestions = reviewModeActive
    ? questions.filter((question, questionIndex) => {
        const answerKey = getQuestionKey(question, questionIndex);
        const reviewMeta = getReviewQuestionMeta(reviewByQuestionId[answerKey]);

        if (reviewFilter === "correct") {
          return reviewMeta?.color === "success";
        }

        if (reviewFilter === "incorrect") {
          return reviewMeta?.color === "error";
        }

        return true;
      })
    : [];

  if (submitSuccess === true || lessonCompleted === true) {
    const scoreDisplay = buildQuestionnaireScoreDisplayPT(questionnaire, reviewData?.result);
    return (
      <Box sx={{ pt: 3, pb: 2, px: { xs: 2, sm: 3 }, width: 1, boxSizing: "border-box" }}>
        <QuestionnaireFilledNotice
          headline={submitSuccess === true ? learnerLabels.successHeadline : learnerLabels.filledHeadline}
          questionnaireTitle={questionnaire.title}
          compact={reviewModeActive === true && reviewResultsVisible === true}
          scoreDisplay={scoreDisplay}
        />

        {reviewModeActive ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 10 }}>
            <Button
              variant={reviewResultsVisible === true ? "outlined" : "contained"}
              color="primary"
              onClick={() => setReviewResultsVisible((previous) => !previous)}
              sx={{ minWidth: { xs: "100%", sm: 300 } }}
            >
              {reviewResultsVisible === true ? "Ocultar resultado do questionário" : "Mostrar Resultado do questionário"}
            </Button>
          </Box>
        ) : null}

        {reviewModeActive === true && reviewResultsVisible === true ? (
          <>
            <Divider
              sx={{
                my: { xs: 4, sm: 5 },
                borderColor: "divider",
              }}
            />
            <Box sx={{ m: 2, p: { xs: 3, sm: 5 }, borderRadius: "8px" }}>
              <Typography variant="h5" component="h3" sx={{ mb: 3.5, textAlign: "center", fontWeight: 600 }}>
                {getReviewSectionTitle(reviewFilter)}
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 14 }}>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 420 }}>
                  <Select size="small" value={reviewFilter} onChange={(event) => setReviewFilter(event.target.value)}>
                    <MenuItem value="all">Mostrar todas as respostas</MenuItem>
                    <MenuItem value="correct">Mostrar apenas respostas certas</MenuItem>
                    <MenuItem value="incorrect">Mostrar apenas respostas erradas</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {filteredReviewQuestions.map((question, questionIndex) => {
                const answerKey = getQuestionKey(question, questionIndex);
                return (
                  <Paper
                    key={answerKey}
                    id={`questionnaire-field-${answerKey}`}
                    elevation={0}
                    sx={{
                      p: 6,
                      mb: questionIndex < filteredReviewQuestions.length - 1 ? QUESTIONNAIRE_CARDS_MARGIN_BOTTOM : 0,
                      borderRadius: "8px",
                      bgcolor: "background.paper",
                      boxShadow: QUESTIONNAIRE_QUESTION_CARD_BOX_SHADOW,
                    }}
                  >
                    <QuestionBlock
                      fieldGroupId={answerKey}
                      question={question}
                      value={answersByQuestionId[answerKey]}
                      onChange={() => {}}
                      readOnly
                      reviewMeta={getReviewQuestionMeta(reviewByQuestionId[answerKey])}
                    />
                  </Paper>
                );
              })}
            </Box>
          </>
        ) : null}
      </Box>
    );
  }

  const submitAnswersDisabled = isNil(enrollmentId);

  return (
    <Box sx={{ pt: 3, pb: 2, px: { xs: 2, sm: 3 }, width: 1, boxSizing: "border-box" }}>
      <Paper elevation={0} sx={QUESTIONNAIRE_HEADER_CARD_SX}>
        <Typography variant="h5" component="h2" gutterBottom={Boolean(questionnaire.description)}>
          {questionnaire.title}
        </Typography>
        {questionnaire.description ? (
          <Typography variant="body2" color="text.secondary">
            {questionnaire.description}
          </Typography>
        ) : null}
      </Paper>

      {submitError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      ) : null}

      {Object.keys(fieldErrors).length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Preenche os campos obrigatórios assinalados antes de confirmares a submissão.
        </Alert>
      ) : null}

      {questions.map((question, questionIndex) => {
        const answerKey = getQuestionKey(question, questionIndex);
        return (
          <Paper
            key={answerKey}
            id={`questionnaire-field-${answerKey}`}
            elevation={0}
            sx={{
              p: 6,
              mb: questionIndex < questions.length - 1 ? QUESTIONNAIRE_CARDS_MARGIN_BOTTOM : 0,
              borderRadius: "8px",
              bgcolor: "background.paper",
              boxShadow: QUESTIONNAIRE_QUESTION_CARD_BOX_SHADOW,
            }}
          >
            <QuestionBlock
              fieldGroupId={answerKey}
              question={question}
              value={answersByQuestionId[answerKey]}
              onChange={(next) => handleAnswerChange(answerKey, next)}
              errorHelperText={fieldErrors[answerKey]}
            />
          </Paper>
        );
      })}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: { xs: 5, sm: 6 },
          pt: { xs: 4, sm: 5 },
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={submitAnswersDisabled}
          onClick={handleClickSubmit}
          sx={{
            minWidth: { xs: "100%", sm: 320 },
            maxWidth: 480,
            py: 1.75,
            px: 4,
            fontSize: "1.0625rem",
            fontWeight: 600,
          }}
        >
          {learnerLabels.submitButton}
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="questionnaire-submit-dialog-title"
        PaperProps={{ className: "sp-modal__paper" }}
      >
        <DialogContent className="sp-modal">
          <div className="sp-modal__head">
            <span className="sp-modal__icon">
              <AlertCircleOutline />
            </span>
            <h2 id="questionnaire-submit-dialog-title" className="sp-modal__title">
              {learnerLabels.dialogTitle}
            </h2>
          </div>
          <div className="sp-modal__body">{learnerLabels.dialogBody}</div>
        </DialogContent>
        <DialogActions className="sp-modal__actions">
          <button type="button" className="sp-modal__btn sp-modal__btn--ghost" onClick={handleConfirmClose} disabled={isSubmitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="sp-modal__btn sp-modal__btn--primary"
            onClick={handleConfirmSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "A submeter…" : "Submeter"}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        aria-labelledby="questionnaire-error-dialog-title"
        aria-describedby="questionnaire-error-dialog-desc"
        PaperProps={{ className: "sp-modal__paper" }}
      >
        <DialogContent className="sp-modal">
          <div className="sp-modal__head">
            <span className="sp-modal__icon">
              <AlertCircleOutline />
            </span>
            <h2 id="questionnaire-error-dialog-title" className="sp-modal__title">
              Erro ao submeter
            </h2>
          </div>
          <div id="questionnaire-error-dialog-desc" className="sp-modal__body">
            {errorDialogMessage}
          </div>
        </DialogContent>
        <DialogActions className="sp-modal__actions">
          <button type="button" className="sp-modal__btn sp-modal__btn--primary" onClick={handleErrorDialogClose} autoFocus>
            OK
          </button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuestionnaireLesson;
